/*
 * TODO: MemberDataRecord needs to be shared from shared package as
 * long as lmt, web concierge or functions still use this type directly.
 */
import { ServiceLanguage } from "@albertine/i18n";
import { z } from "zod";
import { Email, LoginSentinelValue, Name, PhoneNumber } from "./login";
import { Membership } from "./membership.firestore";
import {
    AgentId,
    AlbertineServiceAgentId,
    EmptyString,
    FirebaseId,
    ISODate,
    LongString,
    MediumLengthString,
    MediumLongString,
    MemberId,
    ShortString,
    VeryShortString,
} from "./primitives";
import { Contributions, FirebaseTimestamp, Link } from "./record.firestore";
import { Tenant } from "./tenant";

export const archiveReasonLabels: [string, string][] = [
    ["inactive", "Inactive"],
    ["trial_ended", "Trial ended"],
    ["subscription_canceled", "Subscription canceled"],
    ["android_user", "Android user"],
    ["payment_overdue", "Payment overdue"],
    ["member_misconduct", "Member misconduct"],
    ["other", "Other"],
];

export const archiveReasons: readonly [string, ...string[]] = archiveReasonLabels.map(
    (l) => l[0],
) as unknown as readonly [string, ...string[]];

export const ArchiveReason = z.enum(archiveReasons);
export type ArchiveReason = z.infer<typeof ArchiveReason>;

/**
 * Sentinel value to distinguish between:
 * - null/undefined: Title field was not filled (optional, skipped)
 * - PREFER_NOT_TO_USE_TITLE: User explicitly chose "Prefer not to use"
 * - string value: User selected or entered a title
 */
export const PREFER_NOT_TO_USE_TITLE = "PREFER_NOT_TO_USE";

export const defaultMemberSource = "introducer";
export const MemberSourceId = z.enum([
    "albertine-employee",
    defaultMemberSource,
    "investor-pool",
    "partnership",
    "member-referral",
    "event",
    "shareholder-referral",
    "other",
]);
export type MemberSourceId = z.infer<typeof MemberSourceId>;
export const MemberSourceIdMap: Record<MemberSourceId, string> = {
    "albertine-employee": "Albertine Employee",
    introducer: "Introducer",
    "investor-pool": "Investor Pool",
    partnership: "Partnership",
    "member-referral": "Member Referral",
    event: "Event",
    "shareholder-referral": "Shareholder Referral",
    other: "Other",
};

export const MemberSource = z.object({
    source: MemberSourceId.optional().nullable(),
    additionalNoteOnSource: z.string().nullable().optional(),
});

const MemberDataBody = z.object({
    id: MemberId,
    conversation: FirebaseId,
    calendarId: z.string().nullable(),
    calendarUserEmail: Email.optional().nullable(),
});

export const OnboardingCompletedVia = z.enum(["app", "phone"]);
export type OnboardingCompletedVia = z.infer<typeof OnboardingCompletedVia>;

const MemberDataSystemTimeStamps = z.object({
    createdAt: FirebaseTimestamp,
    updatedAt: FirebaseTimestamp.nullable().optional(),
    lastSeenAt: FirebaseTimestamp.nullable().optional(),
    invitationEmailSentAt: FirebaseTimestamp.nullable().optional(),
    outOfOfficeMessageSent: FirebaseTimestamp.nullable().optional(),
    onboardingBookCallMessageSent: FirebaseTimestamp.nullable().optional(),
    onboardingPlaceRequestMessageSent: FirebaseTimestamp.nullable().optional(),
    onboardingCompletedAt: FirebaseTimestamp.nullable().optional(),
    onboardingCompletedVia: OnboardingCompletedVia.optional().nullable(),
    onboardingNoteId: FirebaseId.optional().nullable(),
});

export const MemberDataDeviceLocation = z.object({
    latitude: z.number(),
    longitude: z.number(),
    place: z
        .object({
            city: z.string(),
            isoCountryCode: z.string(),
        })
        .optional()
        .nullable(),
    updatedAt: FirebaseTimestamp.nullable().optional(),
});

export type MemberDataDeviceLocation = z.infer<typeof MemberDataDeviceLocation>;

export const MemberDataPushNotifications = z.object({
    deviceId: z.string().min(1).optional(),
    pushNotificationsToken: z.string().min(1).optional(),
    pushNotificationsEnabled: z.boolean().optional(),
    pushNotificationsEnabledWeb: z.boolean().optional(),
});

export const MemberDataPushNotificationDeviceTokens = z.object({
    pushNotificationsDeviceTokens: z.array(MemberDataPushNotifications).optional(),
});

export type MemberDataPushNotificationDeviceTokens = z.infer<
    typeof MemberDataPushNotificationDeviceTokens
>;

export const MemberDataMemberUpdatables = z.object({
    title: z.string().optional().nullable(),
    firstName: Name.optional().nullable(),
    lastName: Name.optional().nullable(),
    appVersion: VeryShortString.optional().nullable(),
    appVersionAndroid: VeryShortString.optional().nullable(),
    androidVersion: VeryShortString.optional().nullable(),
    androidDevice: ShortString.optional().nullable(),
    iosVersion: VeryShortString.optional().nullable(),
    iosDevice: ShortString.optional().nullable(),
    webUserAgent: LongString.optional().nullable(),
    hasVisitedChat: z
        .boolean()
        .refine((x) => x) // only allow sending value true, if value is sent
        .optional(),
    hasSeenChatPopover: z.boolean().optional(),
    deviceLocation: MemberDataDeviceLocation.optional().nullable(),
    locationEnabledWeb: z.boolean().optional(),
    lastRatedRequest: z.string().optional().nullable(),
    hasLoggedIn: z.boolean().optional().nullable(),
    serviceLanguage: ServiceLanguage.optional().nullable(),
    email: Email.or(LoginSentinelValue.email).optional().nullable(),
    phoneNumber: PhoneNumber.or(LoginSentinelValue.phoneNumber).optional().nullable(),
});

export const LimitedFamilyMemberProfile = z.object({
    id: z.uuid(),
    type: z.literal("limited_profile"),
    firstName: Name,
    lastName: Name,
    relationship: ShortString.min(3),
    mustKnow: LongString.optional().nullable(),
    dateOfBirth: ISODate.or(EmptyString).optional().nullable(),
    passportName: MediumLengthString.optional().nullable(),
    passportNumber: ShortString.optional().nullable(),
    passportExpiryDate: ISODate.or(EmptyString).optional().nullable(),
    nationality: ShortString.optional().nullable(),
    frequentFlyerNumber: LongString.optional().nullable(),
});

export const LinkedFamilyMemberProfile = z.object({
    id: z.uuid(),
    type: z.literal("linked_profile"),
    member: MemberId,
});

export const FamilyMember = z.discriminatedUnion("type", [
    LimitedFamilyMemberProfile,
    LinkedFamilyMemberProfile,
]);

// AI Summary for rolling member insights
export const MemberAiSummary = z
    .object({
        summary: LongString,
        preferences: z.object({
            dining: z.array(z.string()).optional(),
            travel: z.array(z.string()).optional(),
            entertainment: z.array(z.string()).optional(),
            lifestyle: z.array(z.string()).optional(),
        }),
        version: z.number(),
        lastUpdated: FirebaseTimestamp,
        lastUpdatedBy: AlbertineServiceAgentId,
        messageCountAtUpdate: z.number().optional(),
    })
    .optional()
    .nullable();

export type MemberAiSummary = z.infer<typeof MemberAiSummary>;

export const MemberEligibilityDetails = z.object({
    bin: VeryShortString.optional().nullable(),
    last4: VeryShortString.optional().nullable(),
});

export type MemberEligibilityDetails = z.infer<typeof MemberEligibilityDetails>;

export const MemberPreferredCommunicationChannel = z.enum([
    "app",
    "whatsapp",
    "email",
    "phone-call",
]);

export type MemberPreferredCommunicationChannel = z.infer<
    typeof MemberPreferredCommunicationChannel
>;

export const MemberPreferredContactSlot = z.enum(["primary", "secondary"]);

export type MemberPreferredContactSlot = z.infer<typeof MemberPreferredContactSlot>;

const MemberDataGeneral = z
    .object({
        tenant: Tenant,
        tenantDetails: z.record(z.string(), z.unknown()).optional().nullable(),

        title: VeryShortString.optional().nullable(),
        firstName: Name,
        lastName: Name,
        phoneNumber: PhoneNumber.or(LoginSentinelValue.phoneNumber),
        email: Email.or(LoginSentinelValue.email),

        personalHighlight: LongString.optional().nullable(),

        familyMustKnow: LongString.optional().nullable(),
        personalMustKnow: LongString.optional().nullable(),
        diningPreferences: LongString.optional().nullable(),
        travelMustKnow: LongString.optional().nullable(),
        eventsAndExperiences: LongString.optional().nullable(),
        luxuryItemsAndCollectibles: LongString.optional().nullable(),
        wellnessMustKnow: LongString.optional().nullable(),
        paymentsMustKnow: LongString.optional().nullable(),
        paymentsPreferredCurrency: MediumLongString.optional().nullable(),

        diningFuturePlans: z
            .object({
                plans: LongString,
                updatedAt: FirebaseTimestamp,
                updatedBy: AgentId.or(AlbertineServiceAgentId),
                updatedName: ShortString,
            })
            .optional()
            .nullable(),
        travelFuturePlans: z
            .object({
                plans: LongString,
                updatedAt: FirebaseTimestamp,
                updatedBy: AgentId.or(AlbertineServiceAgentId),
                updatedName: ShortString,
            })
            .optional()
            .nullable(),

        // AI-generated rolling summary
        aiSummary: MemberAiSummary,

        secondaryPhone: PhoneNumber.optional().nullable(),
        secondaryEmail: Email.optional().nullable(),
        preferredCommunicationChannel:
            MemberPreferredCommunicationChannel.optional().nullable(),
        channelContact: MemberPreferredContactSlot.optional().nullable(),

        address: MediumLengthString.optional().nullable(),
        postalCode: VeryShortString.optional().nullable(),
        city: ShortString.optional().nullable(),
        state: ShortString.optional().nullable(),
        country: ShortString.optional().nullable(),
        timeZone: VeryShortString.optional().nullable(),
        nationality: ShortString.optional().nullable(),
        dateOfBirth: ISODate.or(EmptyString).optional().nullable(),
        passportName: MediumLengthString.optional().nullable(),
        passportNumber: ShortString.optional().nullable(),
        passportExpiryDate: ISODate.or(EmptyString).optional().nullable(),
        airlinePreferences: LongString.optional().nullable(),
        frequentFlyerNumber: LongString.optional().nullable(),
        occupation: LongString.optional().nullable(),
        memberships: LongString.optional().nullable(),

        interests: LongString.optional().nullable(),
        loves: LongString.optional().nullable(),
        hates: LongString.optional().nullable(),

        foodAllergies: LongString.optional().nullable(),

        links: z.array(Link).optional().nullable(),
        familyMembers: z.array(FamilyMember).optional().nullable(),

        assignedTo: AgentId.optional().nullable(),
        agentFirstName: ShortString.optional().nullable(),
        assignedToName: ShortString.optional().nullable(),
        contributions: Contributions.optional().nullable(),
        referralId: z.string().optional().nullable(),
        eligibilityDetails: MemberEligibilityDetails.optional().nullable(),
        paymentsCustomerId: z.string().optional().nullable(),
        paymentsCurrency: z.string().optional().nullable(),

        deviceLocation: MemberDataDeviceLocation.optional().nullable(),

        /**
         * @deprecated Use API-computed `confirmedBookingsCount` instead.
         * No longer maintained by Cloud Functions.
         */
        confirmedBookings: z.array(z.string()).optional().nullable(),

        membership: Membership.optional().nullable(),
        groups: z.array(z.string()).optional().nullable(),
    })
    .and(MemberSource);

const MemberDataAssignedTo = z.object({
    assignedTo: AgentId.optional().nullable(),
    agentFirstName: ShortString.optional().nullable(),
    assignedToName: ShortString.optional().nullable(),
});
export type MemberDataAssignedTo = z.infer<typeof MemberDataAssignedTo>;

const MemberDataArchived = z.object({
    archived: z.boolean().optional().nullable(),
    archivedDate: FirebaseTimestamp.nullable().optional(),
    archiveReason: ArchiveReason.optional().nullable(),
    archivedBy: z.string().optional().nullable(),
    reactivatedAt: FirebaseTimestamp.nullable().optional(),
    reactivatedBy: z.string().optional().nullable(),
});

const MemberDataRatingScore = z.object({
    average: z.number(),
    ratingsCount: z.number(),
});

export type MemberDataRatingScore = z.infer<typeof MemberDataRatingScore>;

export const MemberDataMeta = z.object({
    /**
     * @deprecated Use API-computed `activeRequests` on `MemberListItem` instead.
     * No longer maintained by Cloud Functions.
     */
    activeRequests: z.number().default(0).nullable().optional(),
    /**
     * @deprecated Use API-computed `totalRequests` on `MemberListItem` instead.
     * No longer maintained by Cloud Functions.
     */
    totalRequests: z.number().default(0).nullable().optional(),
    bookingsScore: MemberDataRatingScore.optional().nullable(),
    serviceScore: MemberDataRatingScore.optional().nullable(),
});
export type MemberDataMeta = z.infer<typeof MemberDataMeta>;

const MemberKeycloakData = z.object({
    keycloakUserId: z.string().optional(),
});

export const MemberDataFirestoreRecordDoc = MemberDataBody.and(
    MemberDataSystemTimeStamps,
)
    .and(MemberDataPushNotifications)
    .and(MemberDataPushNotificationDeviceTokens)
    .and(MemberDataMemberUpdatables)
    .and(MemberDataGeneral)
    .and(MemberDataAssignedTo)
    .and(MemberDataArchived)
    .and(MemberDataMeta)
    .and(MemberSource)
    .and(MemberKeycloakData);

export const MemberDataFirestoreRecord = z
    .object({ id: MemberId })
    .and(MemberDataFirestoreRecordDoc);
export type MemberDataFirestoreRecord = z.infer<typeof MemberDataFirestoreRecord>;
export type MemberDataFirestoreRecordDoc = z.infer<typeof MemberDataFirestoreRecordDoc>;

export const MemberListItemDeviceLocationPlace = z
    .object({
        city: z.string(),
        isoCountryCode: z.string(),
    })
    .nullable();

export const MemberListItem = z.object({
    id: MemberId,
    conversationId: FirebaseId,
    tenant: Tenant,
    title: VeryShortString.nullable(),
    firstName: Name,
    lastName: Name,
    createdAt: z.string().datetime(),
    lastSeenAt: z.string().datetime().nullable(),
    assignedTo: AgentId.nullable(),
    archived: z.boolean().nullable(),
    activeRequests: z.number().int(),
    totalRequests: z.number().int(),
    confirmedBookingsCount: z.number().int(),
    city: ShortString.nullable(),
    country: ShortString.nullable(),
    deviceLocationPlace: MemberListItemDeviceLocationPlace,
    groups: z.array(z.string()).nullable(),
    membership: Membership.nullable(),
});
export type MemberListItem = z.infer<typeof MemberListItem>;
export type FamilyMember = z.infer<typeof FamilyMember>;
export type LimitedFamilyMemberProfile = z.infer<typeof LimitedFamilyMemberProfile>;
export type LinkedFamilyMemberProfile = z.infer<typeof LinkedFamilyMemberProfile>;

export const AllowedMemberUpdates = z.intersection(
    MemberDataPushNotifications,
    MemberDataMemberUpdatables,
);

export type AllowedMemberUpdates = z.infer<typeof AllowedMemberUpdates>;
